import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Typography,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import LoadingButton from '@mui/lab/LoadingButton';
import { DragDropContext } from 'react-beautiful-dnd';
import { PickTeamResult } from '../types';
import { base64ToObject, objectToBase64, pickRandomCaptain } from '../utils';
import PlayerGroupCard from './PlayerGroupCard';
import PickTeamTeamCard from './PickTeamTeamCard';
import { RollingCaptains } from './RollingCaptains';

export const PickTeam = () => {
  const { resultCode } = useParams<{ resultCode: string }>();
  const navigate = useNavigate();
  const [pickResult, setPickResult] = useState<PickTeamResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isRolling, setIsRolling] = useState(false);

  useEffect(() => {
    try {
      const result = base64ToObject<PickTeamResult>(resultCode);
      setPickResult(result);
    } catch (error: any) {
      setError(error);
    }
  }, [resultCode]);

  if (error || !pickResult) {
    return <Typography>Invalid code</Typography>;
  }

  const updateURL = () => {
    const newCode = objectToBase64(pickResult);
    navigate(`/pick-teams/${newCode}`);
  };

  const handleDragPlayer = (result: any) => {
    if (!result.destination) {
      return;
    }
    const droppableId = result.destination.droppableId;
    if (!droppableId.startsWith('team:')) {
      return;
    }
    // Drag within the same steam
    if (droppableId === result.source.droppableId) {
      return;
    }
    const teamName = droppableId.split(':')[1];
    const team = pickResult.teams.find((team) => team.captain === teamName);
    if (!team) {
      return;
    }
    const playerName = result.draggableId;
    const appearances = result.source.droppableId.split(':')[0];
    const sourceGroupIndex = result.source.droppableId.split(':')[1];

    // Add player to the destination team
    team.players.push({
      name: playerName,
      appearances,
      // No stats
    });

    // Sort the player of the team by appearances from most to least
    team.players.sort((a, b) => b.appearances - a.appearances);

    // Remove player from the group
    const group = pickResult.groups.find(
      (_, index) => index === parseInt(sourceGroupIndex)
    );

    if (group) {
      const playerIndex = group.players.findIndex(
        (player) => player.name === playerName
      );
      if (playerIndex >= 0) {
        group.players.splice(playerIndex, 1);
      }
    }

    // If there is only one cap left, mark him as current so we don't have to roll
    if (pickResult.pickedCaptains.length === pickResult.teams.length - 1) {
      const remainingCap = pickResult.teams.find(
        (team) => !pickResult.pickedCaptains.includes(team.captain)
      )?.captain;
      pickResult.prevLastCaptain = remainingCap;
      pickResult.currentCaptain = remainingCap;
      pickResult.pickedCaptains.push(remainingCap!);
    } else {
      // Mark current cap as undefined to lock the team after the player is already picked
      pickResult.currentCaptain = undefined;
    }

    updateURL();
  };

  const handleShowStatsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    pickResult.showStats = event.target.checked;
    updateURL();
  };

  const handleRoll = () => {
    setIsRolling(true);
    const timeout =
      pickResult.pickedCaptains.length === pickResult.teams.length - 1
        ? 10
        : 500;

    setTimeout(() => {
      if (
        pickResult.pickedCaptains.length === pickResult.teams.length ||
        pickResult.pickRound === 0
      ) {
        pickResult.pickRound = pickResult.pickRound + 1;
        if (pickResult.pickedCaptains.length === pickResult.teams.length) {
          pickResult.pickedCaptains = [];
        }
      }
      const captain = pickRandomCaptain(pickResult);
      if (!pickResult.pickedCaptains.length) {
        pickResult.prevFirstCaptain = captain;
      }
      if (pickResult.pickedCaptains.length === pickResult.teams.length - 1) {
        pickResult.prevLastCaptain = captain;
      }
      pickResult.pickedCaptains.push(captain!);
      pickResult.currentCaptain = captain;
      setIsRolling(false);
      updateURL();
    }, timeout);
  };

  const isGroupDisabled = (appearances: number) => {
    // Return true for any groups that have higher appearances than this number
    return pickResult.groups.some(
      (group) => group.appearances > appearances && group.players.length > 0
    );
  };

  const appearanceLevels = Array.from(
    new Set(pickResult.groups.map((group) => group.appearances))
  )
    .sort()
    .reverse();
  const activeAppearanceLevel = pickResult.groups.find(
    (group) => group.players.length > 0
  )?.appearances;
  const activeAppearanceIndex = activeAppearanceLevel
    ? appearanceLevels.indexOf(activeAppearanceLevel)
    : appearanceLevels.length;
  const isDone = activeAppearanceIndex === appearanceLevels.length;
  const totalPlayersByAppearance = pickResult.groups.reduce(
    (acc, group) => {
      acc[group.appearances] =
        (acc[group.appearances] || 0) + group.players.length;
      return acc;
    },
    {} as Record<number, number>
  );

  return (
    <Stack direction="column" spacing={4}>
      <Stepper activeStep={activeAppearanceIndex} alternativeLabel>
        {appearanceLevels.map((level) => (
          <Step key={level}>
            <StepLabel>
              Appearances: {level}
              <br />({totalPlayersByAppearance[level]} players)
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Paper
        variant="outlined"
        sx={{
          padding: 2,
          marginTop: '16px !important',
          width: 300,
          alignSelf: 'center',
          textAlign: 'center',
        }}
      >
        {pickResult.startDate && (
          <Typography variant="body2">
            Start date: {pickResult.startDate}
          </Typography>
        )}
        <Typography variant="body2">
          Length: {pickResult.numberOfWeeks} weeks
        </Typography>
      </Paper>
      <Paper
        variant="outlined"
        sx={{
          padding: 4,
          width: 600,
          backgroundColor: '#a5d6a7',
          alignSelf: 'center',
        }}
      >
        <Stack direction="column" justifyContent="center" alignItems="center">
          {!isDone && (
            <>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Round {pickResult.pickRound}
              </Typography>
              {pickResult.currentCaptain && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 2,
                    color: isRolling ? '#757575' : 'inherit',
                  }}
                >
                  {pickResult.currentCaptain} picks
                </Typography>
              )}
              <LoadingButton
                variant="contained"
                startIcon={<SportsSoccerIcon />}
                loading={isRolling}
                onClick={handleRoll}
                disabled={isRolling || !!pickResult.currentCaptain}
              >
                Roll the ball
              </LoadingButton>
              <RollingCaptains
                allCaptains={pickResult.teams.map((team) => team.captain)}
                ignoredCaptains={pickResult.pickedCaptains}
                chosenCaptain={pickResult.currentCaptain}
                startRolling={isRolling}
              />
            </>
          )}
          {isDone && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <SportsSoccerIcon />
              <Typography variant="h5">Complete</Typography>
              <SportsSoccerIcon />
            </Stack>
          )}
        </Stack>
      </Paper>
      {!isDone && (
        <FormControlLabel
          sx={{ alignSelf: 'center' }}
          control={
            <Switch
              checked={pickResult.showStats}
              onChange={handleShowStatsChange}
            />
          }
          label="Show player stats (goals/assists)"
        />
      )}
      <DragDropContext onDragEnd={handleDragPlayer}>
        <Stack
          direction={{
            xs: 'column',
            sm: 'column',
            md: 'column',
            lg: 'row',
          }}
          sx={{
            justifyContent: 'space-evenly',
          }}
        >
          {pickResult.groups.map((group, index) => (
            <PlayerGroupCard
              index={index}
              appearances={group.appearances}
              players={group.players}
              key={index}
              isActive={group.appearances === activeAppearanceLevel}
              isDisabled={isGroupDisabled(group.appearances)}
              showStats={pickResult.showStats}
            />
          ))}
        </Stack>
        <Stack
          direction={{
            xs: 'column',
            sm: 'column',
            md: 'column',
            lg: 'row',
          }}
          sx={{
            justifyContent: 'space-evenly',
          }}
        >
          {pickResult.teams.map((team, index) => (
            <PickTeamTeamCard
              captain={team.captain}
              isDropDisabled={
                team.captain !== pickResult.currentCaptain && !isDone
              }
              isDone={isDone}
              players={team.players}
              key={index}
            />
          ))}
        </Stack>
      </DragDropContext>
    </Stack>
  );
};
